import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { getUserDetailsByID, postBankDetails } from '../../../actions/users';
import { closeModalAction } from '../../../actions/modal';
import { isBoltOrg } from '../../../utils/common';

const formName = 'updateUser';

const UpdateUserBankDetails = ({ user_id }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const userDetailsById = useSelector((state) => state.user.userDetailsById);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const methods = useForm({ defaultValues: {} });

    useEffect(() => {
        dispatch(getUserDetailsByID({ user_id }));
    }, [user_id, dispatch]);

    const memoizedUserDetails = useMemo(() => {
        let data = { user: {}, org: {} };
        if (userDetailsById[user_id]) {
            data = userDetailsById[user_id];
        }
        return data;
    }, [userDetailsById, user_id]);

    // For Bolt org users, default the Account Holder Name to the user's display name
    useEffect(() => {
        if (newLoggedUserData?.org?.id === isBoltOrg && memoizedUserDetails?.user?.full_name) {
            methods.setValue('holder_name', memoizedUserDetails.user.full_name);
        }
    }, [newLoggedUserData?.org?.id, memoizedUserDetails?.user?.full_name, methods]);


    // For Bolt org users, the Account Holder Name must match the user's display name
    const validateHolderName = (value) => {
        const requiredName = memoizedUserDetails?.user?.full_name;
        if (newLoggedUserData?.org?.id === isBoltOrg && requiredName && value !== requiredName) {
            return `Account Holder Name must be ${requiredName}`;
        }
        return true;
    };

    const submit = (formData) => {
        const bankDetails = {
            ...(formData.bank ? { bank: formData.bank } : {}),
            ...(formData.acc_num ? { acc_num: formData.acc_num } : {}),
            ...(formData.ifsc ? { ifsc: formData.ifsc } : {}),
            ...(formData.holder_name ? { holder_name: formData.holder_name } : {}),
            ...(formData.upi_vpa ? { upi_vpa: formData.upi_vpa } : {})
        }

        dispatch(postBankDetails({
            userId: user_id,
            bank_details: bankDetails,
            callUserDetails: false,
            closeModal: true,
            formName
        }));
    };

    return (
        <Form
            methods={methods}
            onSubmit={submit}
            formName={formName}
            submitButtonText={'Update'}
            onCancel={() => dispatch(closeModalAction())}
            showSubmit
            submitContainerClassName='flex gap-2 justify-end py-4 px-4'
            isLoading={formButtonLoading[formName]}
        >
            {memoizedUserDetails?.user?.bank ? <div className="bg-white rounded-lg p-4 mb-4 px-6">
                <h3 className="text-lg font-semibold mb-1 text-scogoprimary">Current Bank Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <p className="text-sm text-gray-600">Bank Name</p>
                        <p className="font-medium">{memoizedUserDetails?.user?.bank?.bank_name || 'Not provided'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Account Number</p>
                        <p className="font-medium">{memoizedUserDetails?.user?.bank?.account_number || 'Not provided'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">IFSC Code</p>
                        <p className="font-medium">{memoizedUserDetails?.user?.bank?.ifsc_code || 'Not provided'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Account Holder Name</p>
                        <p className="font-medium">{memoizedUserDetails?.user?.bank?.account_holder_name || 'Not provided'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">UPI ID</p>
                        <p className="font-medium">{memoizedUserDetails?.user?.bank?.upi_id || 'Not provided'}</p>
                    </div>
                </div>
            </div> : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3">
                <Input
                    label="Bank Name"
                    name="bank"
                    placeholder="Enter bank name"
                    required={methods.watch('bank') || methods.watch('acc_num') || methods.watch('ifsc') || methods.watch('holder_name')}
                    className="w-full"
                />
                <Input
                    label="Account Number"
                    name="acc_num"
                    placeholder="Enter account number"
                    required={methods.watch('bank') || methods.watch('acc_num') || methods.watch('ifsc') || methods.watch('holder_name')}
                    className="w-full"
                />
                <Input
                    label="IFSC Code"
                    name="ifsc"
                    placeholder="Enter IFSC code"
                    required={methods.watch('bank') || methods.watch('acc_num') || methods.watch('ifsc') || methods.watch('holder_name')}
                    className="w-full"
                />
                <Input
                    label="Account Holder Name"
                    name="holder_name"
                    placeholder="Enter account holder name"
                    required={methods.watch('bank') || methods.watch('acc_num') || methods.watch('ifsc') || methods.watch('holder_name')}
                    validate={validateHolderName}
                    className="w-full"
                />
                <Input
                    label="UPI VPA"
                    name="upi_vpa"
                    placeholder="Enter UPI ID"
                    required={!methods.watch('bank') && !methods.watch('acc_num') && !methods.watch('ifsc') && !methods.watch('holder_name')}
                    className="w-full"
                />
            </div>
        </Form>
    );
};


export default UpdateUserBankDetails;
